import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Stack,
  Box,
  InputAdornment,
  TextField,
  Typography,
  Divider,
  FormHelperText,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useEffect, useState } from 'react'
import { OrganizationTable } from './organization-table'
import { useOrganizationsQuery } from 'services/organizations'
import { useSearch } from 'hooks/useSearch'
import { useRowSelection } from 'hooks/useRowSelection'
import { Loading } from 'components/Loading'
import { useCreateInvitationsMutation } from 'services/invitations'
import { OrganizationType } from 'types/organization'
import { FileUpload } from 'components/FileUpload'
import { TFunction } from 'i18next'

const MAX_FILE_SIZE = 1000000

const createSchema = (t: TFunction) =>
  z.object({
    subject: z.string().min(1),
    message: z.string().min(1),
    attachmentFileId: z.string(),
  })

type FormFields = {
  subject: string
  message: string
  attachmentFileId: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  tenderId: string
}

export const DialogInviteOrganization = ({ isOpen, onClose, tenderId }: Props) => {
  const { t } = useTranslation()
  const schema = createSchema(t)

  const [searchNameQuery, setSearchNameQuery] = useState('')
  const [searchEmailQuery, setSearchEmailQuery] = useState('')
  const [searchTaxIdQuery, setSearchTaxIdQuery] = useState('')

  const { query: debouncedSearchNameQuery } = useSearch(searchNameQuery)
  const { query: debouncedSearchEmailQuery } = useSearch(searchEmailQuery)
  const { query: debouncedSearchTaxIdQuery } = useSearch(searchTaxIdQuery)

  const args = {
    type: OrganizationType.CLIENT,
    name: debouncedSearchNameQuery,
    email: debouncedSearchEmailQuery,
    taxId: debouncedSearchTaxIdQuery,
  } as const

  const { data: organizations, isLoading } = useOrganizationsQuery(args, {
    refetchOnMountOrArgChange: true,
  })

  const [createInvitations, { isSuccess }] = useCreateInvitationsMutation()

  const rowSelection = useRowSelection({
    totalRows: organizations?.info.totalRows || 0,
  })

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    clearErrors,
  } = useForm<FormFields>({
    defaultValues: {
      subject: '',
      message: '',
      attachmentFileId: '',
    },
    resolver: zodResolver(schema),
  })

  const onSubmit = ({ subject, message, attachmentFileId }: FormFields) => {
    createInvitations({
      message,
      subject,
      tenderId,
      attachmentFileId,
      recipientOrganizationIds: rowSelection.selectedRows,
    })
  }

  useEffect(() => {
    if (isSuccess) {
      onClose()
    }
  }, [onClose, isSuccess])

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth onClose={onClose}>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: 'bold' }}>
        {t('invite')}
      </DialogTitle>
      <DialogCloseButton handleClose={onClose} />
      <Stack px={1} spacing={2}>
        <Box>
          <Controller
            name="subject"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label={t('subject')}
                onChange={field.onChange}
              />
            )}
          />
        </Box>
        <Box>
          <Controller
            name="message"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                minRows={3}
                size="small"
                label={t('invitationText')}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="attachmentFileId"
            control={control}
            render={({ field }) => (
              <FileUpload
                text={t('creationOrderFile')}
                handleFile={(file) => {
                  clearErrors('attachmentFileId')
                  field.onChange(file.id)
                }}
                fileId={field.value}
                sizeLimit={MAX_FILE_SIZE}
                setError={setError}
              />
            )}
          />
          {errors.attachmentFileId && (
            <FormHelperText error>
              {String(errors.attachmentFileId?.message)}
            </FormHelperText>
          )}
        </Box>
        <Stack spacing={1}>
          <Divider flexItem />
          <Typography fontWeight="bold" variant="body1">
            {t('organizations')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('search-based-on-name')}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              value={searchNameQuery}
              onChange={(event) => setSearchNameQuery(event.target.value)}
            />
            <TextField
              label={t('search-based-on-email')}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              value={searchEmailQuery}
              onChange={(event) => setSearchEmailQuery(event.target.value)}
            />
            <TextField
              label={t('search-based-on-tax-id')}
              size="small"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              value={searchTaxIdQuery}
              onChange={(event) => setSearchTaxIdQuery(event.target.value)}
            />
          </Stack>
          {isLoading ? (
            <Loading fullPage={false} />
          ) : (
            <OrganizationTable
              rowSelection={rowSelection}
              organizations={organizations?.items ?? []}
            />
          )}
        </Stack>
      </Stack>
      <DialogActions>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid}
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
        >
          {t('invite')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
